(function () {
  'use strict';

  const ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const PCVD = window.PCVehicleData;
  const collator = PCVD.collator;

  /* ── Vehicle list (shared by all instances) ───────────────────
     PCVehicleData (pc-vehicle-data.js) fetches the visitor's market list once per page and
     shares it with acv, trade-up, etc. Requested when the browser is idle after load, or as
     soon as a selector is touched — then every instance builds from the same data. */
  let PRODUCTS = [];
  let makeTree = {};
  let allMakes = [];
  /* Make name → collection handle, used to match the per-instance priority makes */
  let makeHandleOf = {};
  let catalogReady = false;

  /* list comes sorted by make, then model */
  function buildCatalog(list) {
    PRODUCTS = list;
    makeTree = {};
    makeHandleOf = {};
    PRODUCTS.forEach(p => {
      if (!makeTree[p.make]) makeTree[p.make] = [];
      makeTree[p.make].push(p);
      makeHandleOf[p.make] = p.makeHandle;
    });
    allMakes = Object.keys(makeTree);
    catalogReady = true;
  }

  function loadCatalog() {
    return PCVD.loadProducts().then(list => {
      if (!catalogReady) buildCatalog(list);
    });
  }

  /* ── All fitments (alladmindata.json) ────────────────────────
     Never fetched on page load — only when a visitor clicks the
     "All Fitments" button. PCFitments (pc-fitments.js) fetches it once
     and shares it with every instance. */
  const FIT_SEARCH_LIMIT = 150;
  const PCF = window.PCFitments;

  const ARROW_SVG = `
    <svg class="pc-vd__item-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M4.5 2.5L9.5 7L4.5 11.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  function productUrl(handle, variantId) {
    const base = `${ROUTES_ROOT}products/${encodeURIComponent(handle)}`;
    return variantId ? `${base}?variant=${variantId}` : base;
  }

  function starsHtml(rating) {
    const val = parseFloat(rating);
    if (!rating || isNaN(val)) return '';
    const full = Math.round(val);
    return '★'.repeat(full) + '☆'.repeat(Math.max(0, 5 - full));
  }

  /* Body/year label; engine is appended only when the same body appears more than once */
  function variantLabels(rows) {
    const bodyCount = {};
    rows.forEach(r => { bodyCount[r.body] = (bodyCount[r.body] || 0) + 1; });
    return rows.map(r => (bodyCount[r.body] > 1 && r.engine) ? `${r.body} · ${r.engine}` : r.body);
  }

  /* ── One selector instance ───────────────────────────────────── */
  function initVD(root) {
    let CFG = {};
    try {
      CFG = JSON.parse(root.querySelector('.pc-vd__config').textContent);
    } catch (_) { /* fall through to defaults */ }
    const splitterBest = CFG.splitterBest || 'BEST SELLERS';
    const splitterAll  = CFG.splitterAll  || 'ALL MAKES';
    const fitUrl       = PCF ? CFG.allFitmentsUrl || '' : '';
    const fitPageUrl   = CFG.allFitmentsPageUrl || `${ROUTES_ROOT}pages/all-fitments`;
    const fitLabel     = CFG.allFitmentsLabel || 'All Fitments';

    /* UI text from locale keys custom.vehicle_selector.* — vehicle data is never translated.
       [make] / [name] placeholders are filled at render time. */
    const T = Object.assign({
      searchPlaceholder:       'Search make or model…',
      searchModelsPlaceholder: 'Search [make] models…',
      searchAllPlaceholder:    'Search all makes and models…',
      makes:                   'MAKES',
      models:                  'MODELS',
      allMakes:                'All Makes',
      backTo:                  'Back to [name]',
      backToAllMakes:          'Back to all makes',
      backToVehicleList:       'Back to vehicle list',
      fitmentsLoadError:       'Could not load fitments. Please try again.',
    }, CFG.i18n || {});
    /* function replacer so a "$" in store data is never treated as a pattern */
    const fill = (text, key, value) => text.replace(`[${key}]`, () => value);
    /* Best sellers = the products picked in the section setting (not "has a rating") */
    const bestSellerHandles = new Set(CFG.bestSellerHandles || []);

    /* Priority by collection handle (section setting) — independent of how make names are written */
    const priorityMakeHandles = new Set(CFG.priorityMakeHandles || []);
    const isPriority = m => priorityMakeHandles.has(makeHandleOf[m]);

    /* ── DOM refs ─────────────────────────────────────────────── */
    const $ = sel => root.querySelector(sel);
    const el = {
      trigger:         $('.pc-vd__trigger'),
      panel:           $('.pc-vd__panel'),
      nav:             $('.pc-vd__nav'),
      search:          $('.pc-vd__search'),
      searchWrap:      $('.pc-vd__search-wrap'),
      searchClear:     $('.pc-vd__search-clear'),
      list:            $('.pc-vd__list'),
      loading:         $('.pc-vd__loading'),
      empty:           $('.pc-vd__empty'),
      emptyQuery:      $('.pc-vd__empty-query'),
      redirect:        $('.pc-vd__redirect'),
    };

    /* ── State ────────────────────────────────────────────────── */
    /* mode: 'catalog' = store products (window.__PC.products)
             'fitments' = alladmindata.json, opened via the All Fitments button */
    let mode          = 'catalog';
    let activeMake    = null;
    let fitData       = null;
    let fitMake       = null;
    let fitModel      = null;
    let isOpen        = false;
    let isRedirecting = false;

    /* ── UI helpers ───────────────────────────────────────────── */
    function showLoading(on) {
      el.loading.classList.toggle('is-visible', on);
      el.list.style.display = on ? 'none' : '';
    }

    function showEmpty(on, query) {
      el.empty.classList.toggle('is-visible', on);
      el.emptyFitBtn.hidden = mode === 'fitments';
      if (on && query !== undefined) el.emptyQuery.textContent = query;
    }

    function highlight(item, scroll) {
      el.list.querySelectorAll('.pc-vd__item.is-highlighted')
        .forEach(i => i.classList.remove('is-highlighted'));
      item.classList.add('is-highlighted');
      if (scroll) item.scrollIntoView({ block: 'nearest' });
    }

    /* Search scope per level: makes → all, make → its models, variants → hidden */
    function setSearchScope(level) {
      el.search.value = '';
      el.searchClear.hidden = true;
      el.searchWrap.hidden = level === 'variants';
      el.search.placeholder =
        level === 'models'     ? fill(T.searchModelsPlaceholder, 'make', activeMake) :
        level === 'fit-models' ? fill(T.searchModelsPlaceholder, 'make', fitMake.make) :
        level === 'fit-makes'  ? T.searchAllPlaceholder :
                                 T.searchPlaceholder;
    }

    function highlightFirst() {
      const first = el.list.querySelector('.pc-vd__item');
      if (first) highlight(first);
    }

    /* ── Render helpers ───────────────────────────────────────── */
    /* Product/make/variant names come from store data — always set as text, never HTML */
    function nameSpan(text) {
      const span = document.createElement('span');
      span.className = 'pc-vd__item-name';
      span.textContent = text;
      return span;
    }

    function renderSep(text) {
      const li = document.createElement('li');
      li.className = 'pc-vd__sep';
      li.setAttribute('role', 'presentation');
      li.textContent = text;
      el.list.appendChild(li);
    }

    function renderNote(text) {
      const li = document.createElement('li');
      li.className = 'pc-vd__note';
      li.setAttribute('role', 'presentation');
      li.textContent = text;
      el.list.appendChild(li);
    }

    /* extraHtml must be trusted markup — never store data */
    function renderItem(label, onSelect, opts = {}) {
      const li = document.createElement('li');
      li.className = opts.variant ? 'pc-vd__item pc-vd__item--variant' : 'pc-vd__item';
      li.setAttribute('role', 'option');
      li.appendChild(nameSpan(label));
      if (opts.extraHtml) li.insertAdjacentHTML('beforeend', opts.extraHtml);
      if (opts.arrow) li.insertAdjacentHTML('beforeend', ARROW_SVG);
      /* stopPropagation: the list re-renders on select, so the clicked node is
         detached by the time the document handler checks root.contains() */
      li.addEventListener('click', e => { e.stopPropagation(); onSelect(li); });
      li.addEventListener('mouseenter', () => highlight(li));
      el.list.appendChild(li);
      return li;
    }

    function renderMakeItem(make) {
      const li = renderItem(make, () => drillIntoMake(make), { arrow: true });
      li.dataset.make = make;
    }

    function renderModelItem(product, skipMakeName) {
      const label = skipMakeName ? product.model : `${product.make} ${product.model}`;
      /* Every model shows its own Loox rating (from the vehicle list), when it has one */
      const stars = starsHtml(product.rating);
      /* ratingHtml is safe markup: only ★/☆ and parseInt output */
      const ratingHtml = stars ? `
        <span class="pc-vd__item-rating">
          <span class="pc-vd__item-stars" aria-hidden="true">${stars}</span>
          ${product.raters && parseInt(product.raters) > 0
            ? `<span class="pc-vd__item-raters">(${parseInt(product.raters).toLocaleString()})</span>`
            : ''}
        </span>` : '';

      const li = renderItem(label, item => selectModel(product, item), { extraHtml: ratingHtml });
      li.dataset.handle = product.handle;
    }

    function renderVariantItems(variants, product) {
      const rows = variants.map(v => {
        const parts = v.title.split(' / ');
        return {
          body:   parts[3] || v.title,
          engine: parts[4] ? parts[4].replace(/^\(|\)$/g, '') : '',
        };
      });
      variantLabels(rows).forEach((label, i) => {
        renderItem(label, () => navigateTo(productUrl(product.handle, variants[i].id), label), { variant: true });
      });
    }

    /* "All Fitments" — shown at the end of the list and in the no-results state */
    function allFitmentsButton() {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pc-vd__all-fitments';
      btn.textContent = fitLabel;
      btn.addEventListener('click', e => { e.stopPropagation(); openAllFitments(); });
      return btn;
    }

    function renderAllFitmentsItem() {
      if (!fitUrl) return;
      const li = document.createElement('li');
      li.className = 'pc-vd__more';
      li.setAttribute('role', 'presentation');
      li.appendChild(allFitmentsButton());
      el.list.appendChild(li);
    }

    el.emptyFitBtn = allFitmentsButton();
    el.empty.appendChild(el.emptyFitBtn);
    if (!fitUrl) el.emptyFitBtn.remove();

    /* ── Views — catalog ──────────────────────────────────────── */
    function renderMakes() {
      mode = 'catalog';
      el.list.innerHTML = '';
      showEmpty(false);
      showLoading(false);
      activeMake = null;
      updateNav();
      setSearchScope('makes');

      const bestMakes  = allMakes.filter(isPriority);
      const otherMakes = allMakes.filter(m => !isPriority(m));
      if (bestMakes.length) {
        renderSep(splitterBest);
        bestMakes.forEach(m => renderMakeItem(m));
      }
      if (otherMakes.length) {
        renderSep(splitterAll);
        otherMakes.forEach(m => renderMakeItem(m));
      }
      renderAllFitmentsItem();
      highlightFirst();
    }

    function drillIntoMake(make) {
      mode = 'catalog';
      activeMake = make;
      el.list.innerHTML = '';
      showEmpty(false);
      setSearchScope('models');
      updateNav();

      const products = makeTree[make] || [];
      const best  = products.filter(p => bestSellerHandles.has(p.handle));
      const other = products.filter(p => !bestSellerHandles.has(p.handle));

      if (best.length) {
        renderSep(splitterBest);
        best.forEach(p => renderModelItem(p, true));
      }
      if (other.length) {
        renderSep(T.models);
        other.forEach(p => renderModelItem(p, true));
      }
      renderAllFitmentsItem();
      el.list.scrollTop = 0;
      highlightFirst();
    }

    async function selectModel(product, triggerEl) {
      if (isRedirecting) return;

      /* Show loading spinner during fetch */
      showLoading(true);
      if (triggerEl) triggerEl.style.pointerEvents = 'none';

      let variants;
      try {
        const res = await fetch(`${productUrl(product.handle)}.js`, { cache: 'force-cache' });
        if (res.status === 404 && PCF) {
          /* Product page is gone in this market — continue on All Fitments with the make + model */
          showLoading(false);
          if (triggerEl) triggerEl.style.pointerEvents = '';
          navigateTo(PCF.pageUrl(fitPageUrl, { make: product.make, model: product.model }), product.model);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        variants   = data.variants || [];
      } catch (_) {
        /* Fetch failed — fall back to the plain product page */
        variants = [];
      }

      showLoading(false);
      if (triggerEl) triggerEl.style.pointerEvents = '';

      if (!variants.length) {
        navigateTo(productUrl(product.handle), product.model);
        return;
      }

      if (variants.length === 1) {
        navigateTo(productUrl(product.handle, variants[0].id), product.model);
        return;
      }

      /* Model may come from global search (activeMake null) — sync it so
         breadcrumb and back button point to the product's make */
      activeMake = product.make;
      showEmpty(false);
      setSearchScope('variants');
      updateNav(product);
      el.list.innerHTML = '';
      renderSep(`${product.make} ${product.model}`);
      renderVariantItems(variants, product);
      el.list.scrollTop = 0;
      highlightFirst();
      /* Search is hidden — keep keyboard nav alive by focusing the list */
      el.list.focus({ preventScroll: true });
    }

    /* ── Views — all fitments ─────────────────────────────────── */
    async function openAllFitments() {
      mode = 'fitments';
      activeMake = null;
      fitMake = null;
      fitModel = null;
      el.list.innerHTML = '';
      showEmpty(false);
      updateFitNav();
      setSearchScope('fit-makes');

      if (!fitData) {
        showLoading(true);
        try {
          fitData = await PCF.load(fitUrl);
        } catch (_) {
          showLoading(false);
          if (mode === 'fitments' && !fitMake) renderNote(T.fitmentsLoadError);
          return;
        }
        showLoading(false);
        /* Visitor may have navigated away (or typed) while the file was loading */
        if (mode !== 'fitments' || fitMake) return;
        if (el.search.value.trim()) { el.search.dispatchEvent(new Event('input')); return; }
      }
      renderFitMakes();
    }

    function renderFitMakes() {
      mode = 'fitments';
      fitMake = null;
      fitModel = null;
      el.list.innerHTML = '';
      showEmpty(false);
      showLoading(false);
      updateFitNav();
      setSearchScope('fit-makes');

      renderSep(fitLabel);
      fitData.forEach(m => renderItem(m.make, () => renderFitModels(m), { arrow: true }));
      el.list.scrollTop = 0;
      highlightFirst();
    }

    function renderFitModels(makeObj) {
      mode = 'fitments';
      fitMake = makeObj;
      fitModel = null;
      el.list.innerHTML = '';
      showEmpty(false);
      updateFitNav();
      setSearchScope('fit-models');

      renderSep(makeObj.make);
      makeObj.models.forEach(md => renderItem(md.model, () => renderFitVariants(makeObj, md), { arrow: true }));
      el.list.scrollTop = 0;
      highlightFirst();
    }

    function renderFitVariants(makeObj, modelObj) {
      mode = 'fitments';
      fitMake = makeObj;
      fitModel = modelObj;
      el.list.innerHTML = '';
      showEmpty(false);
      updateFitNav();
      setSearchScope('variants');

      const variants = modelObj.variants || [];
      renderSep(`${makeObj.make} ${modelObj.model}`);
      const labels = variantLabels(variants.map(v => ({ body: v.body || '', engine: v['engine-volume'] || '' })));
      variants.forEach((v, i) => {
        renderItem(labels[i], item => selectFitment(makeObj, modelObj, v, labels[i], item), { variant: true });
      });
      el.list.scrollTop = 0;
      highlightFirst();
      el.list.focus({ preventScroll: true });
    }

    /* A fitment picked from All Fitments:
       - the vehicle has a product page in this market → go there with the matching variant
         (the visitor may simply have missed it in the store list);
       - otherwise (404) → /pages/all-fitments, which adds the PC unit to the cart. */
    async function selectFitment(makeObj, modelObj, variant, label, triggerEl) {
      if (isRedirecting) return;
      const fit = {
        make:   makeObj.make,
        model:  modelObj.model,
        body:   variant.body || '',
        engine: variant['engine-volume'] || '',
        socket: variant.socket || '',
      };

      showLoading(true);
      if (triggerEl) triggerEl.style.pointerEvents = 'none';
      const target = await productTargetFor(fit);
      showLoading(false);
      if (triggerEl) triggerEl.style.pointerEvents = '';

      if (target) {
        navigateTo(target, label);
        return;
      }
      PCF.setPending(fit);
      navigateTo(PCF.pageUrl(fitPageUrl, fit), label);
    }

    /* Product page URL for a fitment, or null when the vehicle has no product page here.
       Handle: the store list (exact make + model) when loaded, else "<make>-<model>". */
    async function productTargetFor(fit) {
      const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
      const known = catalogReady && PRODUCTS.find(p => same(p.make, fit.make) && same(p.model, fit.model));
      const handle = known ? known.handle : `${fit.make}-${fit.model}`.trim().toLowerCase().replace(/\s+/g, '-');
      try {
        const res = await fetch(`${productUrl(handle)}.js`, { cache: 'force-cache' });
        if (!res.ok) return null; /* 404 → All Fitments page */
        const data = await res.json();
        /* Variant title: "PC41 / Abarth / 124 Spider / 1st Gen (2017-2020) / (1.4L MultiAir Turbo)" */
        const bare = s => String(s || '').replace(/^\(|\)$/g, '').trim();
        const rows = (data.variants || []).map(v => ({ id: v.id, parts: String(v.title || '').split(' / ') }));
        const byBody = rows.filter(r => same(r.parts[3], fit.body));
        const match = byBody.find(r => same(bare(r.parts[4]), bare(fit.engine))) || (byBody.length === 1 ? byBody[0] : null);
        return productUrl(data.handle || handle, match ? match.id : null);
      } catch (_) {
        return null;
      }
    }

    /* ── Navigation / Breadcrumb ──────────────────────────────── */
    function crumbSep() {
      const sep = document.createElement('span');
      sep.className = 'pc-vd__nav-sep';
      sep.textContent = '›';
      return sep;
    }

    /* crumbs: [{ label, onClick }] — the last one is the active (non-clickable) crumb */
    function renderNav(crumbs, onBack, backLabel) {
      el.nav.innerHTML = '';
      if (!crumbs.length) return;

      /* Back arrow — round button */
      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'pc-vd__nav-back';
      backBtn.setAttribute('aria-label', backLabel);
      backBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
      backBtn.addEventListener('click', e => { e.stopPropagation(); onBack(); });

      /* Breadcrumb trail */
      const wrap = document.createElement('div');
      wrap.className = 'pc-vd__nav-crumbs';
      crumbs.forEach((c, i) => {
        const isLast = i === crumbs.length - 1;
        if (i) wrap.append(crumbSep());
        const span = document.createElement('span');
        span.className = isLast ? 'pc-vd__nav-crumb is-active' : 'pc-vd__nav-crumb';
        span.textContent = c.label;
        if (!isLast && c.onClick) {
          span.addEventListener('click', e => { e.stopPropagation(); c.onClick(); });
        }
        wrap.append(span);
      });

      el.nav.append(backBtn, wrap);
    }

    function updateNav(activeProduct) {
      if (activeProduct) {
        /* Level 3 — variants */
        renderNav([
          { label: T.allMakes, onClick: renderMakes },
          { label: activeMake,  onClick: () => drillIntoMake(activeMake) },
          { label: activeProduct.model },
        ], () => drillIntoMake(activeMake), fill(T.backTo, 'name', activeMake));
      } else if (activeMake) {
        /* Level 2 — models */
        renderNav([{ label: activeMake }], renderMakes, T.backToAllMakes);
      } else {
        renderNav([]);
      }
    }

    function updateFitNav() {
      const crumbs = [{ label: fitLabel, onClick: renderFitMakes }];
      if (fitMake)  crumbs.push({ label: fitMake.make, onClick: () => renderFitModels(fitMake) });
      if (fitModel) crumbs.push({ label: fitModel.model });

      if (fitModel)     renderNav(crumbs, () => renderFitModels(fitMake), fill(T.backTo, 'name', fitMake.make));
      else if (fitMake) renderNav(crumbs, renderFitMakes, fill(T.backTo, 'name', fitLabel));
      else              renderNav(crumbs, renderMakes, T.backToVehicleList);
    }

    /* ── Search ───────────────────────────────────────────────── */
    function resetView() {
      if (mode === 'fitments') {
        if (!fitData) return;
        fitMake ? renderFitModels(fitMake) : renderFitMakes();
      } else {
        activeMake ? drillIntoMake(activeMake) : renderMakes();
      }
    }

    function searchCatalog(q, raw) {
      if (!catalogReady) { showLoading(true); return; } /* still loading — ensureCatalog re-runs the search */
      /* Level 2 — only the active make's models; Level 1 — all makes + models */
      const inMake = !!activeMake;
      const matchMakes = inMake ? [] : allMakes.filter(m => m.toLowerCase().includes(q));
      const matchProducts = (inMake ? makeTree[activeMake] || [] : PRODUCTS).filter(p =>
        p.model.toLowerCase().includes(q) || `${p.make} ${p.model}`.toLowerCase().includes(q)
      );

      if (!matchMakes.length && !matchProducts.length) {
        showEmpty(true, raw);
        return;
      }

      if (matchMakes.length) {
        renderSep(T.makes);
        matchMakes.forEach(m => renderMakeItem(m));
      }
      if (matchProducts.length) {
        renderSep(T.models);
        matchProducts.forEach(p => renderModelItem(p, inMake));
      }
      renderAllFitmentsItem();
      highlightFirst();
    }

    function searchFitments(q, raw) {
      if (!fitData) return; /* still loading — openAllFitments re-runs the search */

      const inMake = !!fitMake;
      const matchMakes = inMake ? [] : fitData.filter(m => m.make.toLowerCase().includes(q));
      const matchModels = [];
      (inMake ? [fitMake] : fitData).forEach(m => m.models.forEach(md => {
        if (md.model.toLowerCase().includes(q) || `${m.make} ${md.model}`.toLowerCase().includes(q)) {
          matchModels.push([m, md]);
        }
      }));

      if (!matchMakes.length && !matchModels.length) {
        showEmpty(true, raw);
        return;
      }

      if (matchMakes.length) {
        renderSep(T.makes);
        matchMakes.forEach(m => renderItem(m.make, () => renderFitModels(m), { arrow: true }));
      }
      if (matchModels.length) {
        renderSep(T.models);
        matchModels.slice(0, FIT_SEARCH_LIMIT).forEach(([m, md]) => {
          renderItem(inMake ? md.model : `${m.make} ${md.model}`, () => renderFitVariants(m, md), { arrow: true });
        });
      }
      highlightFirst();
    }

    el.search.addEventListener('input', () => {
      const raw = el.search.value.trim();
      const q   = raw.toLowerCase();
      el.searchClear.hidden = !q;

      if (!q) {
        resetView();
        return;
      }

      el.list.innerHTML = '';
      showEmpty(false);
      mode === 'fitments' ? searchFitments(q, raw) : searchCatalog(q, raw);
    });

    el.searchClear.addEventListener('click', () => {
      el.search.value = '';
      el.searchClear.hidden = true;
      resetView();
      el.search.focus();
    });

    /* ── Keyboard navigation ──────────────────────────────────── */
    /* Listens on the panel so it works from the search input and from the
       list (focused on the variants step, where search is hidden) */
    el.list.tabIndex = -1;
    el.panel.addEventListener('keydown', e => {
      if (e.target !== el.search && e.target !== el.list) return;

      const items = Array.from(el.list.querySelectorAll('.pc-vd__item'));
      const cur   = el.list.querySelector('.pc-vd__item.is-highlighted');
      const idx   = items.indexOf(cur);
      if (!items.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          highlight(items[idx < items.length - 1 ? idx + 1 : 0], true);
          break;
        case 'ArrowUp':
          e.preventDefault();
          highlight(items[idx > 0 ? idx - 1 : items.length - 1], true);
          break;
        case 'Enter':
          e.preventDefault();
          if (cur) cur.click();
          break;
        case 'Escape':
          closePanel();
          el.trigger.focus();
          break;
      }
    });

    /* ── Navigate to product ──────────────────────────────────── */
    function navigateTo(url, crumbLabel) {
      if (isRedirecting) return;
      isRedirecting = true;

      /* Add selection as last crumb in breadcrumb */
      if (crumbLabel) {
        const crumbs = el.nav.querySelector('.pc-vd__nav-crumbs');
        if (crumbs) {
          const crumb = document.createElement('span');
          crumb.className = 'pc-vd__nav-crumb is-active';
          crumb.textContent = crumbLabel;

          crumbs.append(crumbSep(), crumb);
        }
      }

      el.redirect.classList.add('is-visible');
      el.list.style.pointerEvents = 'none';
      el.search.disabled = true;
      window.location.href = url;
    }

    /* ── Panel open / close ───────────────────────────────────── */
    function openPanel() {
      isOpen = true;
      el.panel.classList.add('is-open');
      el.trigger.setAttribute('aria-expanded', 'true');
      if (catalogReady) {
        renderMakes();
      } else {
        mode = 'catalog';
        activeMake = null;
        el.list.innerHTML = '';
        showEmpty(false);
        updateNav();
        setSearchScope('makes');
        ensureCatalog();
      }
      setTimeout(() => el.search.focus(), 50);
    }

    /* Vehicle list not loaded yet (first open before the idle prefetch finished) */
    function ensureCatalog() {
      showLoading(true);
      loadCatalog().then(() => {
        /* Visitor may have closed the panel or moved to All Fitments meanwhile */
        if (!isOpen || mode !== 'catalog' || activeMake) return;
        showLoading(false);
        if (el.search.value.trim()) el.search.dispatchEvent(new Event('input'));
        else renderMakes();
      }).catch(() => {
        if (!isOpen || mode !== 'catalog') return;
        showLoading(false);
        el.list.innerHTML = '';
        renderNote(T.fitmentsLoadError);
        renderAllFitmentsItem();
      });
    }

    function closePanel() {
      isOpen = false;
      el.panel.classList.remove('is-open');
      el.trigger.setAttribute('aria-expanded', 'false');
      isRedirecting = false;
      showLoading(false);
      el.list.style.pointerEvents = '';
      el.search.disabled = false;
      el.redirect.classList.remove('is-visible');
    }

    /* No stopPropagation here — the document handler below lets the click
       close any other open instance, while this root ignores it */
    ['pointerenter', 'focus', 'touchstart'].forEach(type => {
      el.trigger.addEventListener(type, () => { loadCatalog().catch(() => {}); }, { once: true, passive: true });
    });

    el.trigger.addEventListener('click', () => {
      isOpen ? closePanel() : openPanel();
    });

    document.addEventListener('click', e => {
      if (isOpen && !root.contains(e.target)) closePanel();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && isOpen) { closePanel(); el.trigger.focus(); }
    });

    /* Back button restores the page from bfcache with the redirect overlay
       still up and isRedirecting stuck at true — reset to a clean state */
    window.addEventListener('pageshow', e => {
      if (e.persisted) closePanel();
    });
  }

  /* Script tag is emitted once per snippet render — init each root only once */
  document.querySelectorAll('.pc-vd').forEach(root => {
    if (root.dataset.pcVdReady) return;
    root.dataset.pcVdReady = '1';
    initVD(root);
  });

  /* Prefetch the vehicle list once the page has loaded and the browser is idle,
     so it is usually ready before the first click (never competes with page load) */
  function prefetchCatalog() {
    if (!document.querySelector('.pc-vd') || catalogReady) return;
    const run = () => loadCatalog().catch(() => { /* retried on open */ });
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1500);
  }
  if (document.readyState === 'complete') prefetchCatalog();
  else window.addEventListener('load', prefetchCatalog, { once: true });

})();
